'use client';

/**
 * The hero's picture: a real room, designed in the product, with the system
 * that is in it.
 *
 * The room is the showcase home theater exactly as the editor renders it —
 * its treatment in place — drawn once on a 3x canvas and cut out
 * (`public/home/hero-room.webp`, scripts/render-home-stills.mjs), so the
 * first screen shows the product without paying for a WebGL context before
 * anything is read. The room is material and does not change with the theme,
 * so one render serves both. Two
 * glass cards float over it: the parts list, straight from the catalogue
 * (names and list prices are read at build time, lib/homeShowcase.ts), and
 * the response at the seat, before and after.
 *
 * Motion: each piece rises in after the headline, a stagger step apart; then,
 * as the page scrolls, the room settles back and the cards drift up at their
 * own rates — depth, from `--exit` (useScrollScene), with no React render per
 * frame. Reduced motion shows the resting composition.
 */

import Image from 'next/image';
import { useRef } from 'react';
import type { ShowcaseProduct } from '@/lib/homeShowcase';
import ChecklistMark from '@/components/room/gear/ChecklistMark';
import { PRODUCT_TILE_BG } from '@/components/room/gear/GearImageTile';
import { stagger } from './BlurText';
import { seatCurves } from './curves';
import { formatUsd } from './formatUsd';
import { useScrollScene } from './useScrollScene';

/** The cut-out render's pixel size. */
const ROOM_W = 2400;
const ROOM_H = 1831;
const PLOT = { w: 220, h: 64 } as const;
const CURVES = seatCurves(PLOT.w, PLOT.h);

export default function HeroStage({ system }: { system: readonly ShowcaseProduct[] }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollScene(ref);
  const total = system.reduce((s, p) => s + p.priceUsd, 0);

  return (
    <div ref={ref} data-testid="home-hero-stage" className="rt-scene relative mx-auto mt-12 w-full max-w-[1180px] px-4 sm:mt-16">
      {/* `isolate`: the light below sits at z -1, and needs a stacking
          context here or it lands behind the page itself. */}
      <div className="relative isolate mx-auto w-full max-w-[980px]">
        {/* Light under the room: a pool of the accent and a floor shadow,
            so the render sits in a space rather than on a page. */}
        <div aria-hidden className="hero-room-light pointer-events-none absolute inset-x-[4%] bottom-[-6%] top-[8%] -z-10" />

        <div className="rt-rise-in" style={stagger(6)}>
          <div className="hero-room-depth">
            <Image
              src="/home/hero-room.webp"
              alt="A home theater designed in roomtreatment.diy: tower, center and surround speakers, a subwoofer, the screen, a sofa, and acoustic panels on the walls."
              width={ROOM_W}
              height={ROOM_H}
              priority
              sizes="(min-width: 1024px) 980px, 100vw"
              className="h-auto w-full select-none"
              draggable={false}
            />
          </div>
        </div>

        <div className="rt-rise-in absolute -left-[2%] top-[14%] hidden w-[46%] max-w-[300px] sm:block" style={stagger(8)}>
          <div className="hero-card-near">
            <SystemCard system={system} total={total} />
          </div>
        </div>

        {/* A phone has no room for the parts list over the picture: the
            system becomes a chip under the room — the three renders, the
            check and the total. Docked in the flow, so paper, not glass. */}
        <div className="rt-rise-in mt-2 flex justify-center sm:hidden" style={stagger(8)}>
          <SystemChip system={system} total={total} />
        </div>

        <div className="rt-rise-in absolute -right-[1%] top-[58%] hidden w-[40%] max-w-[290px] sm:block" style={stagger(9)}>
          <div className="hero-card-far">
            <SeatCard />
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemCard({ system, total }: { system: readonly ShowcaseProduct[]; total: number }) {
  return (
    <div className="rt-glass rounded-2xl p-4" data-testid="home-hero-system">
      <p className="px-1 text-overline font-semibold uppercase text-[var(--rt-ink-dim)]">Your system</p>
      <ul className="mt-3 flex flex-col gap-2">
        {system.map((p) => (
          <li key={p.id} className="flex items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg" style={{ background: PRODUCT_TILE_BG }}>
              <Image src={p.image} alt="" width={96} height={96} className="h-[80%] w-[80%] object-contain" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body font-semibold text-[var(--rt-ink)]">{p.name}</span>
              <span className="block truncate text-body-sm text-[var(--rt-ink-dim)]">
                {p.kind} · {formatUsd(p.priceUsd)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-[var(--rt-hairline)] px-1 pt-3">
        <span className="inline-flex items-center gap-2 text-body-sm font-medium text-[var(--rt-ink)]">
          <ChecklistMark state="done" className="h-5 w-5" iconClassName="h-3 w-3" />
          Works together
        </span>
        <span className="text-body font-semibold tabular-nums text-[var(--rt-ink)]">{formatUsd(total)}</span>
      </div>
    </div>
  );
}

function SystemChip({ system, total }: { system: readonly ShowcaseProduct[]; total: number }) {
  return (
    <div className="flex items-center gap-3 rounded-full border border-[var(--app-border)] bg-[var(--app-card)] py-1.5 pl-1.5 pr-4" data-testid="home-hero-system-chip">
      <span className="flex -space-x-2">
        {system.map((p) => (
          <span
            key={p.id}
            className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--rt-raised)]"
            style={{ background: PRODUCT_TILE_BG }}
          >
            <Image src={p.image} alt={p.name} width={80} height={80} className="h-[72%] w-[72%] object-contain" />
          </span>
        ))}
      </span>
      <ChecklistMark state="done" className="h-5 w-5" iconClassName="h-3 w-3" />
      <span className="text-body font-semibold tabular-nums text-[var(--rt-ink)]">{formatUsd(total)}</span>
    </div>
  );
}

function SeatCard() {
  return (
    <div className="rt-glass rounded-2xl p-4" data-testid="home-hero-seat">
      <div className="flex items-center justify-between gap-3">
        <p className="text-overline font-semibold uppercase text-[var(--rt-ink-dim)]">At your seat</p>
        <span className="flex items-center gap-3 text-body-sm text-[var(--rt-ink-dim)]">
          <span className="inline-flex items-center gap-1.5"><span className="h-px w-4 bg-[var(--rt-ink-faint)]" />Before</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded-full bg-[var(--rt-ink)]" />After</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${PLOT.w} ${PLOT.h}`} className="mt-3 h-auto w-full" aria-hidden>
        <line x1={0} y1={PLOT.h / 2} x2={PLOT.w} y2={PLOT.h / 2} stroke="var(--rt-hairline)" strokeDasharray="2 3" />
        <path d={CURVES.before} fill="none" stroke="var(--rt-ink-faint)" strokeWidth={1.2} />
        <path d={CURVES.after} fill="none" stroke="var(--rt-ink)" strokeWidth={2.2} strokeLinecap="round" />
      </svg>
    </div>
  );
}
